import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { apiService } from '../../../services/api';
import type { ClientUpdate, TeamUpdate } from '../../../services/userService';
import { formatDateForInput } from '../../../utils/dateUtils';
import { BasicInfoSection } from './CompanyInfo/BasicInfoSection';
import { ContactInfoSection } from './CompanyInfo/ContactInfoSection';
import { InsurancesSection } from './CompanyInfo/InsurancesSection';
import {
  initialCompanyFormData,
  type WizardFormData,
  wizardCompanySchema,
} from './CompanyInfo/schema';

const EMPTY_OBJECT: Record<string, never> = {};

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export default function Step2CompanyInfo({ onNext, onBack }: Props) {
  const theme = useTheme();
  const { refreshUserDetails, account } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [timezones, setTimezones] = useState<string[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WizardFormData>({
    resolver: zodResolver(wizardCompanySchema),
    defaultValues: initialCompanyFormData,
  });

  useEffect(() => {
    try {
      const supportedTimezones = Intl.supportedValuesOf('timeZone');
      setTimezones(supportedTimezones);
    } catch (_e) {
      const fallbackTimezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Australia/Sydney',
      ];
      setTimezones(fallbackTimezones);
    }
  }, []);

  const formatNumberWithCommas = useCallback((value: string | number): string => {
    if (value === '$' || value === '' || value === null || value === undefined) {
      return '';
    }
    const cleanValue = String(value).replace(/[$,]/g, '');
    if (cleanValue.trim() === '' || Number.isNaN(parseFloat(cleanValue))) {
      return String(value);
    }
    const num = parseFloat(cleanValue);
    return `$${num.toLocaleString('en-US')}`;
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      setIsFetching(true);
      try {
        const details = await apiService.getAccountDetails();
        const accountData = details.account || EMPTY_OBJECT;

        const newFormData: WizardFormData = { ...initialCompanyFormData, ...accountData };

        for (const key of Object.keys(initialCompanyFormData) as (keyof WizardFormData)[]) {
          if (newFormData[key] === undefined || newFormData[key] === null) {
            newFormData[key] = '';
          }
        }

        const dateFields: (keyof WizardFormData)[] = [
          'liability_insurance_expiry',
          'indemnity_insurer_expiry',
          'workers_compensation_expiry_1',
          'workers_compensation_expiry_2',
          'workers_compensation_expiry_3',
          'workers_compensation_expiry_4',
        ];

        for (const field of dateFields) {
          if (newFormData[field]) {
            newFormData[field] = formatDateForInput(newFormData[field] as string);
          }
        }

        const numberFields = [
          'liability_insurance_per_claim',
          'liability_insurance_aggregate',
          'indemnity_insurer_per_claim',
          'indemnity_insurer_aggregate',
        ];

        for (const field of numberFields) {
          const key = field as keyof WizardFormData;
          if (newFormData[key]) {
            newFormData[key] = newFormData[key]?.toString();
          }
        }

        reset(newFormData);
      } catch (_error) {
        // Ignore fetch errors, form will be empty
      } finally {
        setIsFetching(false);
      }
    };
    fetchDetails();
  }, [reset]);

  const onSave = async (data: WizardFormData) => {
    setIsLoading(true);

    const clientFields: (keyof ClientUpdate)[] = ['clientName'];
    const teamFields: (keyof TeamUpdate)[] = Object.keys(data).filter(
      (key) => !clientFields.includes(key as keyof ClientUpdate)
    ) as (keyof TeamUpdate)[];

    const clientDataToSave: ClientUpdate = {};
    if (account?.role !== 'team_lead') {
      for (const field of clientFields) {
        const value = (data as unknown as Record<string, unknown>)[field];
        if (value !== undefined) {
          (clientDataToSave as Record<string, unknown>)[field] = value;
        }
      }
    }

    const teamDataToSave: TeamUpdate = {};
    for (const field of teamFields) {
      const fieldValue = data[field];
      if (fieldValue !== undefined) {
        (teamDataToSave as Record<string, string | number | null>)[field] =
          typeof fieldValue === 'number' ? String(fieldValue) : fieldValue;
      }
    }
    const numberFields: (keyof TeamUpdate)[] = [
      'liability_insurance_per_claim',
      'liability_insurance_aggregate',
      'indemnity_insurer_per_claim',
      'indemnity_insurer_aggregate',
    ];
    for (const field of numberFields) {
      const value = teamDataToSave[field];
      if (value && typeof value === 'string') {
        const parsedValue = parseFloat(value.replace(/[^\d.-]/g, ''));
        teamDataToSave[field] = Number.isNaN(parsedValue) ? null : String(parsedValue);
      }
    }

    try {
      await apiService.updateAccountDetails({ client: clientDataToSave, team: teamDataToSave });
      await refreshUserDetails();
      onNext();
    } catch (_error) {
      showSnackbar('Failed to save company information.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        Tell us about your company
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
        This information helps BIDLOGIQ tailor responses and complete standard forms automatically.
      </Typography>

      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'background.default',
          border: '1px dashed grey',
          mb: 4,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
          * Only <strong>Company Name</strong>, <strong>Primary Business Registration</strong>, and{' '}
          <strong>Company Website</strong> are strictly required to proceed. You can add or update
          the remaining details later via the Company Info page.
        </Typography>
      </Paper>

      <Box component="form" onSubmit={handleSubmit(onSave)}>
        <Grid container direction="column" spacing={3}>
          <BasicInfoSection control={control} errors={errors} timezones={timezones} />
          <ContactInfoSection control={control} errors={errors} />
          <InsurancesSection
            control={control}
            errors={errors}
            formatNumberWithCommas={formatNumberWithCommas}
          />
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={onBack} disabled={isLoading} size="large">
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            size="large"
            sx={{
              fontWeight: 'bold',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.bidlogiq?.rust || 'primary.dark',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Save & Continue'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
